// Classifies registry tokens into the two rows the Model IVS selector shows:
// "Exchange" (surfaces sourced directly from an exchange) and "Generated"
// (surfaces reconstructed from spot/perp venues). The `source` field on each
// AvailableToken is the classifier — no backend change needed.
//
// Kept asset-free (no SVG imports) so it stays a clean, fast unit-test seam.
import type { AvailableToken } from '../../../providers/exotics-tokens/use-available-tokens';

// Sources whose surfaces come straight off an options exchange.
export const EXCHANGE_SOURCES = new Set(['deribit']);
// Sources whose surfaces are generated from spot/perp venues.
export const GENERATED_SOURCES = new Set(['binance', 'mexc']);

export interface RegistryRows {
  exchange: AvailableToken[];
  generated: AvailableToken[];
}

// Partitions the registry into Exchange / Generated rows, preserving registry
// order within each row. Tokens whose source matches neither set are dropped
// rather than silently landing in a row — an unknown source is a data signal,
// not something to guess at.
export const classifyRegistryTokens = (tokens: AvailableToken[]): RegistryRows => {
  const exchange: AvailableToken[] = [];
  const generated: AvailableToken[] = [];

  for (const token of tokens) {
    if (EXCHANGE_SOURCES.has(token.source)) {
      exchange.push(token);
    } else if (GENERATED_SOURCES.has(token.source)) {
      generated.push(token);
    }
  }

  return { exchange, generated };
};

// Computes a display label per token that is unique within its row, in the
// same order as the input. Two tokens in a row that share a baseCcy (e.g.
// HYPE_USDT on binance AND mexc, or BTC_USDT vs BTC_USDC) would otherwise
// render indistinguishable chips.
//   - Default label is baseCcy (falling back to baseQuote when absent).
//   - If that label collides with another token in the row, use baseQuote.
//   - If baseQuote ALSO collides (same pair on two venues), suffix the source.
// Label-only: never derives or mutates the wire token (baseQuote), which the
// snapshot path keys on.
export const labelRegistryRow = (tokens: AvailableToken[]): string[] => {
  const ccyCounts = new Map<string, number>();
  const baseQuoteCounts = new Map<string, number>();
  for (const token of tokens) {
    const ccy = token.baseCcy || token.baseQuote;
    ccyCounts.set(ccy, (ccyCounts.get(ccy) ?? 0) + 1);
    baseQuoteCounts.set(token.baseQuote, (baseQuoteCounts.get(token.baseQuote) ?? 0) + 1);
  }

  return tokens.map((token) => {
    const ccy = token.baseCcy || token.baseQuote;
    if ((ccyCounts.get(ccy) ?? 0) <= 1) return ccy;
    if ((baseQuoteCounts.get(token.baseQuote) ?? 0) <= 1) return token.baseQuote;
    return `${token.baseQuote} (${token.source})`;
  });
};
